/* <feature-catalog:generated>
 * THE CATALOG — every shared feature that can be mounted on a site, described once.
 *
 * GENERATED from packages/docit-features/manifests/*.md — do not hand-edit; run
 * `pnpm registry:sync` after changing a manifest. The drift guard
 * (src/generatedRegistry.test.ts) fails the build if this block and the manifests disagree.
 *
 * A feature belongs here when it lives in `packages/` and could reasonably run on more than
 * one host. App-owned surfaces (slate's 3D space, its documents feature, coil's own document
 * scenes) deliberately stay in their app's route table: a registry that swallowed them would
 * claim a portability nobody has verified.
 *
 * Scenes are loaded through the `~/...` compat aliases every consumer defines, so this file
 * resolves identically in slate and coil with no per-app branching.
 */
import {
  Bell,
  BookOpen,
  BookmarkSimple,
  Books,
  Briefcase,
  CalendarBlank,
  ChartLine,
  ChatsCircle,
  Desktop,
  Envelope,
  GitBranch,
  Graph,
  Handshake,
  IdentificationCard,
  ListBullets,
  Newspaper,
  Question,
  SquaresFour,
  Storefront,
  TreeStructure,
  UsersThree,
} from "@phosphor-icons/react";
import type { FeatureDefinition } from "./types";

export const featureCatalog: FeatureDefinition[] = [
  {
    id: "market",
    title: "Marketplace",
    description:
      "Browse and post listings and projects, quote, and run the transaction spine (quotes, workplans, milestones, reviews, messages).",
    requiredBackend: [
      "marketplace.search",
      "marketplace.listings.mine",
      "marketplace.quotes.submit",
      "marketplace.messages.threads",
    ],
    routes: [
      {
        path: "/market",
        load: () =>
          import("~/features/market/MarketplaceScene").then((m) => ({
            default: m.MarketplaceScene,
          })),
      },
      // Static segments outrank the param routes below — /market/new, /market/post,
      // /market/me, /market/profile and /market/interview all resolve before
      // /market/listing/$id can claim them.
      {
        path: "/market/new",
        load: () =>
          import("~/features/market/NewListingWizardScene").then((m) => ({
            default: m.NewListingWizardScene,
          })),
      },
      {
        path: "/market/post",
        load: () =>
          import("~/features/market/PostWizardScene").then((m) => ({ default: m.PostWizardScene })),
      },
      {
        path: "/market/me",
        load: () =>
          import("~/features/market/MyMarketScene").then((m) => ({ default: m.MyMarketScene })),
      },
      {
        path: "/market/profile",
        load: () =>
          import("~/features/market/ProfileEditorScene").then((m) => ({
            default: m.ProfileEditorScene,
          })),
      },
      {
        path: "/market/interview",
        load: () =>
          import("~/features/market/CvInterviewScene").then((m) => ({
            default: m.CvInterviewScene,
          })),
      },
      {
        path: "/market/listing/$id",
        load: () =>
          import("~/features/market/ListingDetailScene").then((m) => ({
            default: m.ListingDetailScene,
          })),
      },
      {
        path: "/market/listing/$id/edit",
        load: () =>
          import("~/features/market/ListingComposerScene").then((m) => ({
            default: m.ListingComposerScene,
          })),
      },
      {
        path: "/messages",
        load: () =>
          import("~/features/market/MessagesScene").then((m) => ({ default: m.MessagesScene })),
      },
      {
        // A public CV must render with no session at all — hence outside the authed guard.
        path: "/u/$slug",
        public: true,
        load: () => import("./publicProfileRoute").then((m) => ({ default: m.PublicProfileRoute })),
      },
    ],
    nav: [
      { id: "market", title: "Market", icon: Storefront, to: "/market", section: "marketplace" },
      {
        id: "market-mine",
        title: "My listings",
        icon: Briefcase,
        to: "/market/me",
        section: "marketplace",
      },
      {
        id: "market-profile",
        title: "Profile CV",
        icon: IdentificationCard,
        to: "/market/profile",
        section: "marketplace",
      },
      { id: "messages", title: "Messages", icon: ChatsCircle, to: "/messages", section: "people" },
    ],
  },
  {
    id: "people",
    title: "People",
    description: "The people directory, a person's page, and the HR engagement layer.",
    requiredBackend: ["people.list", "engagements.list"],
    routes: [
      {
        path: "/people",
        load: () =>
          import("~/features/people/PeopleScene").then((m) => ({ default: m.PeopleScene })),
      },
      {
        path: "/people/$userId",
        load: () =>
          import("~/features/people/PersonScene").then((m) => ({ default: m.PersonScene })),
      },
      {
        // HR is a place, not a person — its own top-level route.
        path: "/hr",
        load: () => import("~/features/people/HrScene").then((m) => ({ default: m.HrScene })),
      },
    ],
    nav: [
      { id: "people", title: "People", icon: UsersThree, to: "/people", section: "people" },
      { id: "hr", title: "HR", icon: Handshake, to: "/hr", section: "people" },
    ],
  },
  {
    id: "subscribe",
    title: "Subscriptions",
    description:
      "Subscribe to a thing and be told when it changes: the bell primitive, and the panel listing everything you follow.",
    requiredBackend: [
      "subscriptions.status",
      "subscriptions.toggle",
      "subscriptions.mine",
      "subscriptions.kinds",
    ],
    routes: [
      {
        path: "/subscriptions",
        load: () =>
          import("~/features/subscribe/SubscriptionsScene").then((m) => ({
            default: m.SubscriptionsScene,
          })),
      },
    ],
    nav: [
      {
        id: "subscriptions",
        title: "Subscriptions",
        icon: Bell,
        to: "/subscriptions",
        section: "primary",
      },
    ],
  },
  {
    id: "work",
    title: "Work",
    description:
      "Docit Work — the department's tiles (epics, features, stories, tasks, bugs, issues, questions, spikes, incidents): one query grammar, one tile page, one write path.",
    requiredBackend: [
      "projects.context",
      "projects.inputs.set",
      "work.bench.get",
      "work.phased.list",
      "work.items.list",
      "work.items.search",
      "work.items.info",
      "work.items.create",
      "work.projects.list",
      "work.inbox",
      "work.questions.answer",
      "work.now",
      "work.todo",
    ],
    routes: [
      {
        // Bench (work-bench, 2026-10-20): the developer's / project manager's board — widgets they arrange, two
        // presets, decisions as popovers, phased plans (model + effort per phase, the handover, the bag) and
        // sending a tile to an IDE window. Its own top-level path like /plainly: it is where the work is DONE from,
        // not another work list.
        path: "/bench",
        load: () =>
          import("~/features/work/bench/BenchScene").then((m) => ({ default: m.BenchScene })),
      },
      {
        // Project context: ?project=<id>&panel=inputs|atlas|concepts|impact|activity. Existing project
        // work, attached collections and repository observations share the project board. News is an
        // input lens (?input=<collection>&lens=causality etc.), with no separate project application.
        path: "/work",
        load: () => import("~/features/work/WorkScene").then((m) => ({ default: m.WorkScene })),
      },
      // Static segments outrank the param route: /work/inbox and /work/sprints resolve before
      // /work/$key can claim them. The $key route stays LAST in this list — filename order is
      // route precedence (the registry law).
      {
        path: "/work/inbox",
        load: () =>
          import("~/features/work/WorkInboxScene").then((m) => ({ default: m.WorkInboxScene })),
      },
      {
        // The To-do dashboard (Docit Work 23, 2026-09-27): one card per ROOT cause of what waits on the
        // person across every workspace — what it unblocks, why, and the actions that clear it (existing
        // endpoints only). The Work section's landing (nav `work` points here); a static segment, so it
        // resolves before /work/$key.
        path: "/work/todo",
        load: () =>
          import("~/features/work/WorkTodoScene").then((m) => ({ default: m.WorkTodoScene })),
      },
      {
        // The state of things for a reader who is not technical (owner 2026-09-30: "i want this for
        // non technicals — i want the questions asked plainly — then more resolution then more
        // resolution — minimum reading max understanding"). One plain QUESTION per tile with a short
        // answer; opens once into plain consequence, once more into the engineering truth. The words
        // live on the tile at `custom.plain`, so this is a lens over the work items, never a second
        // copy of them. Its own top-level path, not under /work: it is not a work surface, it is the
        // same work explained.
        path: "/plainly",
        load: () =>
          import("~/features/work/PlainlyScene").then((m) => ({ default: m.PlainlyScene })),
      },
      {
        // The now board (2026-09-27): running · waiting on you · ready · queued · blocked · done today,
        // plans with progress, and counts from the same person's other workspaces. Reachable from the
        // To-do page's header (no nav entry of its own); a static segment, so it resolves before /work/$key.
        path: "/work/now",
        load: () =>
          import("~/features/work/WorkNowScene").then((m) => ({ default: m.WorkNowScene })),
      },
      {
        path: "/work/sprints",
        load: () =>
          import("~/features/work/WorkSprintsScene").then((m) => ({ default: m.WorkSprintsScene })),
      },
      {
        // The knowledge aggregator: assemble a stage from the whole workspace, then run the
        // causality engine over exactly what was chosen. A static segment, so it resolves before
        // /work/$key can claim it.
        path: "/work/aggregate",
        load: () =>
          import("~/features/work/KnowledgeAggregatorScene").then((m) => ({
            default: m.KnowledgeAggregatorScene,
          })),
      },
      {
        // The plan review (19/20): every item, flow, node executor and full prompt a plan will run,
        // approved by the owner before anything starts (?project=<KEY>). Two segments, so /work/$key
        // can never claim it; declared before $key anyway to keep that route LAST.
        path: "/work/plan/$planRef",
        load: () =>
          import("~/features/work/WorkPlanScene").then((m) => ({ default: m.WorkPlanScene })),
      },
      {
        path: "/work/$key",
        load: () =>
          import("~/features/work/WorkItemScene").then((m) => ({ default: m.WorkItemScene })),
      },
      {
        // The one public answer origin (D9): a signed single-use token renders one Question Card
        // and accepts one answer through work.public.answer — no session, no shell, no other data.
        path: "/q/$token",
        public: true,
        load: () =>
          import("~/features/work/GuestAnswerScene").then((m) => ({ default: m.GuestAnswerScene })),
      },
    ],
    nav: [
      { id: "bench", title: "Bench", icon: Desktop, to: "/bench", section: "work" },
      { id: "work", title: "To do", icon: SquaresFour, to: "/work/todo", section: "work" },
      { id: "plainly", title: "Plainly", icon: Question, to: "/plainly", section: "work" },
      { id: "work-items", title: "Work items", icon: ListBullets, to: "/work", section: "work" },
      {
        id: "work-aggregate",
        title: "Knowledge aggregator",
        icon: Graph,
        to: "/work/aggregate",
        section: "knowledge",
      },
    ],
  },
  {
    id: "mail",
    title: "Mail",
    description:
      "The Mail area — a person's mail read for what matters: how much it matters and why, what it means, what to do and by when, why it came and what follows, with alerts in the bell and in Telegram where the person asked.",
    requiredBackend: [
      "mail.overview",
      "mail.items.list",
      "mail.items.get",
      "mail.items.mark",
      "mail.todos.list",
      "mail.settings.get",
      "mail.intake.paste",
    ],
    routes: [
      {
        // mail-desk (2026-10-16): Important first, then everything open, to-dos, insights and the
        // honest lanes (suspicious, snoozed, done, archived). A paste reads one mail with nothing
        // connected; Check now pulls every mailbox.
        path: "/mail",
        load: () => import("~/features/mail/MailScene").then((m) => ({ default: m.MailScene })),
      },
      {
        // When to be told (level, Telegram, the bell), how the mail is read (language, thinker),
        // where to-dos go, and the mailboxes (Gmail / Outlook via Settings › Mail, IMAP here, a
        // forwarding address for services). A static segment, so it resolves before /mail/$id.
        path: "/mail/settings",
        load: () =>
          import("~/features/mail/MailSettingsScene").then((m) => ({
            default: m.MailSettingsScene,
          })),
      },
      {
        // One mail: what it says, what it means, what to do, the chain (why it came / what follows),
        // to-dos, the alert trail, the mail itself. ?act=handled (a Telegram card's Done button)
        // marks it done once. The $id route stays LAST.
        path: "/mail/$id",
        load: () =>
          import("~/features/mail/MailItemScene").then((m) => ({ default: m.MailItemScene })),
      },
    ],
    nav: [
      { id: "mail", title: "Mail", icon: Envelope, to: "/mail", section: "primary" },
    ],
  },
  {
    id: "share",
    title: "Share links",
    description:
      "Signed share grants: the public document door at /p/$urlId, and the grant list that manages it.",
    requiredBackend: [
      "documents.read.viaGrant",
      "documents.comments.viaGrant",
      "documents.comment.viaGrant",
      "share-grants.list",
      "share-grants.create",
      "share-grants.update",
      "share-grants.revoke",
    ],
    routes: [
      {
        // The signed document door. A share grant IS the authorization: the server re-validates the
        // token, its expiry, its revocation and its password on EVERY call, and answers a soft 200
        // {status} envelope so an anonymous reader is never logged out or bounced. The page reaches
        // exactly one document plus its comment thread, with author PII withheld server-side.
        path: "/p/$urlId",
        public: true,
        load: () => import("./grantDocumentRoute").then((m) => ({ default: m.GrantDocumentRoute })),
      },
    ],
  },
  {
    id: "git",
    title: "Git repositories",
    description:
      "Connect a git repository to the workspace or to a project, browse its committed structure one level at a time, and read a file — history is read, never written.",
    requiredBackend: [
      "git.connections.list",
      "git.connections.create",
      "git.connections.tree",
      "git.connections.file",
    ],
    routes: [
      {
        // Every connected repository: the structure (left) and the connections with their verbs
        // (right). coil has no sidebar, so this page IS its 'structure in the menu'.
        path: "/repositories",
        load: () =>
          import("~/features/git/RepositoriesScene").then((m) => ({
            default: m.RepositoriesScene,
          })),
      },
      {
        // One repository at a path: `?path=` (connection-relative) and an optional `?kind=file|dir`
        // hint. A search param, not a splat, so a file named like a route segment is never one.
        path: "/repo/$connectionId",
        load: () =>
          import("~/features/git/RepositoryScene").then((m) => ({ default: m.RepositoryScene })),
      },
    ],
    nav: [
      {
        id: "repositories",
        title: "Repositories",
        icon: GitBranch,
        to: "/repositories",
        section: "work",
      },
    ],
  },
  {
    id: "analysis",
    title: "Knowledge tools",
    description:
      "Concepts, causality and visual tools for established workspace and project knowledge.",
    requiredBackend: ["analysis.scope", "analysis.inputs", "analysis.read"],
    routes: [
      {
        path: "/knowledge/stories",
        load: () =>
          import("~/features/analysis/stories/StoryMagazine").then((m) => ({
            default: m.StoryMagazine,
          })),
      },
      {
        path: "/knowledge/explore",
        load: () =>
          import("~/features/analysis/KnowledgeExplorer").then((m) => ({
            default: m.KnowledgeExplorer,
          })),
      },
      {
        path: "/knowledge/analysis",
        load: () =>
          import("~/features/analysis/AnalysisScene").then((m) => ({ default: m.AnalysisScene })),
      },
      {
        path: "/knowledge/analysis/$view",
        load: () =>
          import("~/features/analysis/AnalysisScene").then((m) => ({ default: m.AnalysisScene })),
      },
      {
        path: "/knowledge/visuals",
        load: () =>
          import("~/features/analysis/VisualsHub").then((m) => ({ default: m.VisualsHub })),
      },
      {
        path: "/knowledge/visuals/causal-map",
        load: () =>
          import("~/features/analysis/causalMap/CausalMapScene").then((m) => ({
            default: m.CausalMapScene,
          })),
      },
      {
        path: "/knowledge/visuals/$viewId",
        load: () =>
          import("~/features/analysis/VisualsHub").then((m) => ({ default: m.VisualRoute })),
      },
    ],
    nav: [
      {
        id: "living-stories",
        title: "Living stories",
        icon: BookOpen,
        to: "/knowledge/stories",
        section: "knowledge",
      },
      {
        id: "analysis",
        title: "Knowledge tools",
        icon: ChartLine,
        to: "/knowledge/analysis",
        section: "knowledge",
      },
      {
        id: "knowledge-visuals",
        title: "Visualizations",
        icon: SquaresFour,
        to: "/knowledge/visuals",
        section: "knowledge",
      },
    ],
  },
  {
    id: "briefing",
    title: "Knowledge brief",
    description: "A personal magazine of distilled knowledge across input channels.",
    requiredBackend: [
      "briefing.feed",
      "briefing.outlook",
      "briefing.curation",
      "briefing.curation.configure",
      "briefing.curation.check",
      "briefing.preferences.set",
      "briefing.story.set",
    ],
    routes: [
      {
        path: "/briefing/calendar",
        load: () =>
          import("~/features/analysis/BriefingScene").then((m) => ({ default: m.BriefingScene })),
      },
      {
        path: "/briefing/magazine",
        load: () =>
          import("~/features/analysis/BriefingScene").then((m) => ({ default: m.BriefingScene })),
      },
      {
        path: "/briefing/perspectives",
        load: () =>
          import("~/features/analysis/BriefingScene").then((m) => ({ default: m.BriefingScene })),
      },
      {
        path: "/briefing/visuals",
        load: () =>
          import("~/features/analysis/BriefingScene").then((m) => ({ default: m.BriefingScene })),
      },
      {
        path: "/briefing",
        load: () =>
          import("~/features/analysis/BriefingScene").then((m) => ({ default: m.BriefingScene })),
      },
      {
        path: "/briefing/tree",
        load: () =>
          import("~/features/analysis/BriefingScene").then((m) => ({ default: m.BriefingScene })),
      },
      {
        path: "/briefing/canopy",
        load: () =>
          import("~/features/analysis/BriefingScene").then((m) => ({ default: m.BriefingScene })),
      },
      {
        path: "/briefing/topics",
        load: () =>
          import("~/features/analysis/BriefingScene").then((m) => ({ default: m.BriefingScene })),
      },
      {
        path: "/briefing/consequences",
        load: () =>
          import("~/features/analysis/BriefingScene").then((m) => ({ default: m.BriefingScene })),
      },
      {
        path: "/briefing/saved",
        load: () =>
          import("~/features/analysis/BriefingScene").then((m) => ({ default: m.BriefingScene })),
      },
      {
        path: "/briefing/hidden",
        load: () =>
          import("~/features/analysis/BriefingScene").then((m) => ({ default: m.BriefingScene })),
      },
    ],
    nav: [
      {
        id: "briefing",
        title: "Knowledge brief",
        icon: Newspaper,
        to: "/briefing",
        section: "knowledge",
      },
      {
        id: "briefing-calendar",
        title: "Event calendar",
        icon: CalendarBlank,
        to: "/briefing/calendar",
        section: "knowledge",
      },
      {
        id: "briefing-tree",
        title: "Knowledge tree",
        icon: TreeStructure,
        to: "/briefing/tree",
        section: "knowledge",
      },
      {
        id: "briefing-canopy",
        title: "Interest tiles",
        icon: SquaresFour,
        to: "/briefing/canopy",
        section: "knowledge",
      },
      {
        id: "briefing-perspectives",
        title: "Perspectives",
        icon: Books,
        to: "/briefing/perspectives",
        section: "knowledge",
      },
      {
        id: "briefing-magazine",
        title: "Magazine",
        icon: Newspaper,
        to: "/briefing/magazine",
        section: "knowledge",
      },
      {
        id: "briefing-consequences",
        title: "Possible futures",
        icon: ChartLine,
        to: "/briefing/consequences",
        section: "knowledge",
      },
      {
        id: "briefing-saved",
        title: "Saved stories",
        icon: BookmarkSimple,
        to: "/briefing/saved",
        section: "knowledge",
      },
    ],
  },
];
/* </feature-catalog:generated> */
